import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, Clock, Music2, Headphones, Star, Image } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const JMAJOR_LOGO = "https://customer-assets-eiarnc6j.emergentagent.net/job_c5db327b-4bf4-420f-890a-b7ce4db2a362/artifacts/jdkdmeym_Jmajor-Photoroom.png";
const DJ_PHOTO = "https://customer-assets-eiarnc6j.emergentagent.net/job_c5db327b-4bf4-420f-890a-b7ce4db2a362/artifacts/nsvyncms_hf_20260922_132807_5d3a5476-a6be-47ad-98c1-006406a84070.webp";

export default function Music() {
  const [packages, setPackages] = useState([]);
  const [portfolio, setPortfolio] = useState([]);

  useEffect(() => {
    axios.get(`${API}/packages/dj`).then(r => setPackages(r.data.packages)).catch(() => {});
    axios.get(`${API}/dj/portfolio`).then(r => setPortfolio(r.data.images)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen pt-20" data-testid="music-page" style={{ background: 'linear-gradient(135deg, #0a0000 0%, #1a0508 25%, #0d0d0d 50%, #1a0508 75%, #0a0000 100%)' }}>
      {/* Hero - Logo + "DJ Booking" */}
      <section className="relative overflow-hidden py-12 md:py-20">
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-transparent" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.img
            src={JMAJOR_LOGO}
            alt="J MAJOR"
            className="mx-auto h-16 md:h-24 object-contain mb-6"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            data-testid="jmajor-logo"
          />
          <motion.h1
            className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl text-[#EDEDED] mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            DJ <span className="red-gradient-text">Booking</span>
          </motion.h1>
          <motion.p
            className="text-[#A1A1AA] text-base md:text-lg max-w-2xl mx-auto mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            Professional DJ entertainment for every occasion. From intimate gatherings to grand celebrations.
          </motion.p>
          <motion.div
            className="flex items-center justify-center gap-6 text-sm text-[#777]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <span className="flex items-center gap-2"><Headphones className="w-4 h-4 red-glow-text" /> Open-Format DJ</span>
            <span className="flex items-center gap-2"><Music2 className="w-4 h-4 red-glow-text" /> Pro Sound System</span>
            <span className="flex items-center gap-2"><Clock className="w-4 h-4 red-glow-text" /> Up to 8 Hours</span>
          </motion.div>
        </div>
      </section>

      {/* DJ Photo */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <motion.div
          className="rounded-2xl overflow-hidden border border-white/10"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <img
            src={DJ_PHOTO}
            alt="J MAJOR - Live the Vibe. Play the Moment."
            className="w-full object-cover"
            data-testid="dj-photo"
          />
        </motion.div>
      </section>

      {/* Packages */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#EDEDED] mb-3">
            Our <span className="red-gradient-text">Packages</span>
          </h2>
          <p className="text-[#A1A1AA] text-sm">Choose the perfect package for your event</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8" data-testid="dj-packages">
          {packages.map((pkg, index) => (
            <motion.div
              key={pkg.id || pkg.tier}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.15 }}
              className={`relative rounded-2xl p-8 ${
                pkg.highlight
                  ? 'border-2 border-[#EF0000]/50'
                  : 'border border-white/10'
              }`}
              style={{ background: pkg.highlight ? 'linear-gradient(to bottom, rgba(255,23,68,0.08), rgba(10,10,10,0.9))' : 'rgba(10,10,10,0.8)' }}
              data-testid={`dj-package-${index}`}
            >
              {pkg.highlight && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="px-4 py-1 rounded-full text-sm flex items-center gap-1 text-white font-medium" style={{ background: '#EF0000' }}>
                    <Star className="w-4 h-4" fill="currentColor" /> Most Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="font-['Playfair_Display'] text-2xl text-[#EDEDED] mb-1">{pkg.name}</h3>
                <p className="text-xs flex items-center justify-center gap-1.5 red-glow-text">
                  <Clock className="w-3.5 h-3.5" /> {pkg.duration}
                </p>
              </div>

              <div className="text-center mb-6">
                <span className="text-5xl font-['Playfair_Display'] font-bold red-glow-text">
                  £{pkg.price}
                </span>
              </div>

              <p className="text-[#A1A1AA] text-sm text-center mb-6 italic">{pkg.tagline}</p>

              <ul className="space-y-3 mb-8">
                {pkg.features?.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-4 h-4 flex-shrink-0 mt-0.5 red-glow-text" />
                    <span className="text-[#A1A1AA] text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              <Link to="/dj/book" data-testid={`dj-book-${index}`}>
                <Button
                  className={`w-full rounded-full py-6 font-medium ${
                    pkg.highlight
                      ? 'text-white'
                      : 'bg-transparent border-2 text-white hover:text-white'
                  }`}
                  style={pkg.highlight ? { background: '#EF0000' } : { borderColor: '#EF0000', color: '#EF0000' }}
                >
                  Book Now
                </Button>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Disclaimer */}
        <motion.div
          className="mt-10 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <p className="text-xs text-[#52525B] leading-relaxed max-w-2xl mx-auto" data-testid="dj-disclaimer">
            <span className="text-[#A1A1AA] font-medium">*PLEASE NOTE</span> — Music for toasts, announcements and special moments can be arranged in advance. Certain requests and additional requirements may be subject to availability and our Terms & Conditions.
          </p>
        </motion.div>
      </section>

      {/* Our Work - DJ Portfolio */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#EDEDED] mb-3">
            Our <span className="red-gradient-text">Work</span>
          </h2>
          <p className="text-[#A1A1AA] text-sm">Events we've performed at</p>
        </div>

        {portfolio.length === 0 ? (
          <div className="text-center py-12" data-testid="dj-portfolio">
            <Image className="w-12 h-12 mx-auto mb-3 opacity-20 text-[#52525B]" />
            <p className="text-[#52525B] text-sm italic">Gallery coming soon</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="dj-portfolio">
            {portfolio.map((img, index) => (
              <motion.div
                key={img.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative group aspect-square rounded-xl overflow-hidden border border-white/10"
              >
                <img src={img.image_url} alt={img.caption || 'DJ Event'} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
                {img.caption && (
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-white text-sm">{img.caption}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* Testimonials */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="relative rounded-2xl overflow-hidden border border-white/10">
          <img
            src="https://customer-assets-eiarnc6j.emergentagent.net/job_c5db327b-4bf4-420f-890a-b7ce4db2a362/artifacts/m4e1lypy_composite%20%281%29%20-%20Copy.png"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/70" />
          <div className="relative z-10 py-16 px-6">
            <div className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#EDEDED] mb-3">
                <span className="red-gradient-text">Testimonials</span>
              </h2>
              <p className="text-[#A1A1AA] text-sm">What Our Clients Say</p>
            </div>

            <div className="text-center py-8" data-testid="dj-testimonials">
              <p className="text-[#A1A1AA]/80 text-sm italic">Reviews coming soon</p>
            </div>
          </div>
        </div>
      </section>

      {/* My Journey */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="my-journey-section">
        <div className="relative rounded-2xl overflow-hidden border border-white/10">
          <img
            src="https://customer-assets-eiarnc6j.emergentagent.net/job_c5db327b-4bf4-420f-890a-b7ce4db2a362/artifacts/vi92866j_afaff.png"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/75" />
          <div className="relative z-10 py-16 px-6 md:px-12">
            <div className="text-center mb-10">
              <motion.h2
                className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#EDEDED] mb-3"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                My <span className="red-gradient-text">Journey</span>
              </motion.h2>
            </div>

            <motion.div
              className="max-w-3xl mx-auto space-y-5"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.15 }}
            >
              <p className="text-[#D4D4D8] text-sm md:text-base leading-relaxed">
                Music has always been a part of who I am. From a young age, my mother enrolled me in music classes, and that small beginning shaped much of my life since. I started by learning the keyboard, and over time I had the privilege of performing in churches, at concerts, and eventually with bands, including a couple of two-man projects.
              </p>
              <p className="text-[#D4D4D8] text-sm md:text-base leading-relaxed">
                I also developed a love for singing, which allowed me to perform on stages, record in studios, and share my music on platforms like YouTube and Spotify.
              </p>
              <p className="text-[#D4D4D8] text-sm md:text-base leading-relaxed">
                In 2016, I began exploring DJing, teaching myself using apps on my phone and sharing some of my early sets on YouTube. It quickly became something I truly enjoyed, and it also led me into music production, where I've had the pleasure of creating a few original tracks of my own.
              </p>
              <p className="text-[#D4D4D8] text-sm md:text-base leading-relaxed">
                I'm now looking forward to growing further in this journey, and would be honoured to bring my music to events, clubs, and weddings.
              </p>

              <div className="flex items-center justify-center gap-5 pt-4">
                <a
                  href="https://www.youtube.com/channel/UCdiyxQXA5pf7yNfX9YKBPNg"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="journey-youtube-link"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 bg-white/5 hover:bg-[#FF0000]/15 hover:border-[#FF0000]/40 transition-all duration-300 group"
                >
                  <svg className="w-5 h-5 text-[#FF0000] group-hover:scale-110 transition-transform" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                  <span className="text-[#D4D4D8] text-sm font-medium">YouTube</span>
                </a>
                <a
                  href="https://open.spotify.com/artist/6IopKkGTD3ujeqpK8dkVlr?si=fa90d8004cfa452a"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="journey-spotify-link"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 bg-white/5 hover:bg-[#1DB954]/15 hover:border-[#1DB954]/40 transition-all duration-300 group"
                >
                  <svg className="w-5 h-5 text-[#1DB954] group-hover:scale-110 transition-transform" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>
                  <span className="text-[#D4D4D8] text-sm font-medium">Spotify</span>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
